import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, Copy, Check, Rocket, AlertTriangle, FileText, RotateCcw, RefreshCw, Power, List, KeyRound, Search } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '@/components/theme-provider';
import railwayIcon from '../assets/icons/railway.svg';

const RailwayIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [exampleService, setExampleService] = useState('');
  const [exampleDeveloper, setExampleDeveloper] = useState('');
  const [lastWebhookAt, setLastWebhookAt] = useState<string | null>(null);
  const [webhookEventCount, setWebhookEventCount] = useState(0);

  const [apiToken, setApiToken] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookSecretConfigured, setWebhookSecretConfigured] = useState(false);
  const [savingSecret, setSavingSecret] = useState(false);

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getRailwayStatus();
      if (data?.connected) {
        setConnected(true);
        setLastWebhookAt(data.last_webhook_at || null);
        setWebhookEventCount(data.webhook_event_count || 0);
        setWebhookSecretConfigured(data.webhook_secret_configured || false);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectRailway({ api_token: apiToken });
      setSuccessMsg('Railway connected successfully!');
      setApiToken('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectRailway();
      setConnected(false);
      setSuccessMsg('Railway disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testRailway();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Railway (${data.project_count ?? 0} project${data.project_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveWebhookSecret = async () => {
    setError(null);
    setSavingSecret(true);
    try {
      await apiClient.saveRailwayWebhookSecret({ webhook_secret: webhookSecret });
      setWebhookSecret('');
      setWebhookSecretConfigured(true);
      setSuccessMsg('Webhook secret saved. Kestrel will only accept Railway events that include this secret.');
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to save webhook secret');
    } finally {
      setSavingSecret(false);
    }
  };

  const baseWebhookUrl = `${window.location.origin}/api/webhooks/railway`;
  const webhookUrl = webhookSecretConfigured ? `${baseWebhookUrl}?secret=YOUR_SECRET` : baseWebhookUrl;

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}

      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={railwayIcon} alt="Railway" className="w-7 h-7 object-contain" style={{ filter: isDark ? 'invert(1)' : 'none' }} />
              <div>
                <CardTitle className="flex items-center gap-2">
                  Railway
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Deployment monitoring, log analysis, rollback, redeploy, and variable automation</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Webhook Events</span>
                <p className="font-medium">{webhookEventCount.toLocaleString()} event{webhookEventCount !== 1 ? 's' : ''}</p>
              </div>
              {lastWebhookAt && (
                <div>
                  <span className="text-muted-foreground">Last Webhook</span>
                  <p className="font-medium">{new Date(lastWebhookAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Webhook Setup
              {lastWebhookAt ? (
                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">{webhookEventCount.toLocaleString()} event{webhookEventCount !== 1 ? 's' : ''} received</Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground text-xs font-normal">No events yet</Badge>
              )}
            </CardTitle>
            <CardDescription>To use Railway triggers (Deployment Failed, Deployment Crashed), configure each Railway project to send events to Kestrel. Webhooks are configured per project.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Webhook URL</p>
              <div className="flex items-center gap-2">
                <code className="bg-background px-2.5 py-1.5 rounded text-sm border flex-1 truncate select-all">{webhookUrl}</code>
                <Button variant="outline" size="sm" onClick={() => {
                  navigator.clipboard.writeText(webhookUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}>
                  {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
              {webhookSecretConfigured && (
                <p className="text-xs text-muted-foreground mt-1.5">Replace <code className="bg-background px-1 rounded">YOUR_SECRET</code> with the secret you saved below. Railway does not sign webhooks, so the secret in the URL is how Kestrel attributes events to your account.</p>
              )}
            </div>

            <ol className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">1</span>
                <span>Open a Railway <strong className="text-foreground">Project</strong> you want to monitor.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">2</span>
                <span>Click <strong className="text-foreground">Settings</strong> (top-right) and open the <strong className="text-foreground">Webhooks</strong> tab.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">3</span>
                <span>Paste the <strong className="text-foreground">Webhook URL</strong> above. Railway delivers all deployment status changes and alerts across every environment in the project.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">4</span>
                <span>Click <strong className="text-foreground">Save Webhook</strong>. Repeat for each project you want Kestrel to monitor.</span>
              </li>
            </ol>

            <div className="bg-muted/50 p-4 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Webhook Secret <span className="normal-case font-normal">(recommended)</span></p>
                {webhookSecretConfigured && (
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Configured</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Railway does not sign webhooks. Set a secret here and append <code className="bg-background px-1 rounded">?secret=YOUR_SECRET</code> to the webhook URL so Kestrel can verify and route events to your account. Without a secret, events are accepted based on URL secrecy alone.</p>
              <div className="flex items-center gap-2">
                <Input
                  type="password"
                  value={webhookSecret}
                  onChange={e => setWebhookSecret(e.target.value)}
                  placeholder={webhookSecretConfigured ? '••••••••••••••••' : 'Choose a random secret string'}
                  className="flex-1"
                />
                <Button size="sm" onClick={handleSaveWebhookSecret} disabled={savingSecret || !webhookSecret}>
                  {savingSecret ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {webhookSecretConfigured ? 'Update' : 'Save'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these Railway actions and triggers in your workflows to automate deployment monitoring and incident response.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Deployment Failed', desc: 'Triggers when a Railway deployment fails to build or deploy.', Icon: XCircle },
                  { name: 'Deployment Crashed', desc: 'Triggers when a deployment crashes after starting.', Icon: AlertTriangle },
                  { name: 'Deployment Succeeded', desc: 'Triggers when a deployment completes successfully and is live.', Icon: CheckCircle2 },
                  { name: 'Volume Alert', desc: 'Triggers when a volume approaches its capacity threshold.', Icon: AlertTriangle },
                  { name: 'Resource Alert', desc: 'Triggers when CPU/RAM usage exceeds configured thresholds.', Icon: AlertTriangle },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Get Deployment', desc: 'Retrieve status, URL, and git metadata for a deployment.', Icon: Rocket },
                  { name: 'Get Deployment Logs', desc: 'Fetch build and runtime logs to diagnose failures and crashes.', Icon: FileText },
                  { name: 'Rollback', desc: 'Roll a service back to its previous successful deployment.', Icon: RotateCcw },
                  { name: 'Redeploy', desc: 'Re-run the latest deployment to recover from transient failures.', Icon: RefreshCw },
                  { name: 'Restart', desc: 'Restart a running deployment without rebuilding.', Icon: Power },
                  { name: 'List Deployments', desc: 'List recent deployments for a service, filtered by status.', Icon: List },
                  { name: 'Set Variables', desc: 'Create or update service variables and secrets.', Icon: KeyRound },
                  { name: 'Investigate Railway', desc: 'AI investigates deploy failures, crashes, and configuration issues.', Icon: Search },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a Railway deployment fails for the{' '}
              <input
                value={exampleService}
                onChange={e => setExampleService(e.target.value)}
                placeholder="service"
                style={{ width: `${Math.max(exampleService.length || 7, 7)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}
              service, get the deploy logs, DM the{' '}
              <input
                value={exampleDeveloper}
                onChange={e => setExampleDeveloper(e.target.value)}
                placeholder="developer"
                style={{ width: `${Math.max(exampleDeveloper.length || 9, 9)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}
              on Slack with the root-cause summary, and create a Jira bug ticket.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleService || !exampleDeveloper}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a Railway deployment fails for the ${exampleService} service, get the deploy logs, investigate the root cause, DM ${exampleDeveloper} on Slack with the summary, and create a Jira bug ticket.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={railwayIcon} alt="Railway" className="w-7 h-7 object-contain" style={{ filter: isDark ? 'invert(1)' : 'none' }} />
              <div>
                <CardTitle>Connect Railway</CardTitle>
                <CardDescription>Connect your Railway account to enable deployment monitoring, log analysis, rollback, and variable automation in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="rw-token" className="text-sm">API Token</Label>
              <Input
                id="rw-token"
                type="password"
                value={apiToken}
                onChange={e => setApiToken(e.target.value)}
                placeholder="Your Railway account token"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Create one at <strong className="text-foreground">railway.com/account/tokens</strong> (Account Settings → Tokens). Leave the <strong className="text-foreground">Workspace</strong> dropdown set to <strong className="text-foreground">"No workspace"</strong> to mint an account-scoped token, which can read projects, services, deployments, and logs across your account. The token is sent as a Bearer token to Railway's GraphQL API.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !apiToken}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Railway
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RailwayIntegration;
